import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  Customer,
  CustomerRelation,
  CustomerSearchResult,
  getRelationTypeDisplay
} from '../../../../../../../models/customer';
import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { ListResponseCountOptional } from '../../../../../../../models/list-response';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../utils';
import * as moment from 'moment';

@Component({
  selector: 'app-add-linked-account-modal',
  templateUrl: './add-linked-account-modal.component.html',
  styleUrls: ['./add-linked-account-modal.component.css']
})
export class AddLinkedAccountModalComponent implements OnInit {

  @Input() customer: Customer;
  @Input() savedCallback: () => undefined;

  relation: CustomerRelation = {
    id: null,
    url: null,
    related_customer_id: null,
    related_customer_url: null,
    related_customer_email: null,
    related_customer_first_name: null,
    related_customer_last_name: null,
    related_customer_birthday: null,
    related_customer_profile_image_url: null,
    related_customer_profile_image_small_url: null,
    relation: null,
    created_at: null,
    updated_at: null
  };

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public loadingRelationSubmit = false;
  public errors: any = {};
  public errorMessage = '';

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public getRelationTypeDisplay = getRelationTypeDisplay;
  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      map(() => { this.loadingCustomers = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    // Reset the selected customer.
    this.relation.related_customer_id = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.results.filter(
      customer => customer.id !== this.customer.id
    );
  }

  /**
   * Handle a click on one of the customer results by adding it to the relation
   * and displaying the customer's name in the search input.
   */
  public selectRelatedCustomer(customer: CustomerSearchResult) {
    this.relation.related_customer_id = customer.id;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
  }

  /**
   * Whether a valid set of inputs for a relation have been selected. Used to
   * determine whether the save button is clickable.
   */
  public validInputSelected() {
    return (
      (this.relation.related_customer_id !== null) &&
      (this.relation.relation !== null) &&
      // @ts-ignore (angular enters a string if you select the null option)
      (this.relation.relation !== 'null')
    );
  }

  public handleSave() {
    this.loadingRelationSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    this.customerService
      .addCustomerRelation(
        this.customer.id,
        this.relation
      )
      .then(createdRelation => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to create customer links.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRelationSubmit = false; });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

}
